<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { useRouter } from 'vue-router'
  import { ref, computed, onMounted, watch } from 'vue'
  import { ROUTES } from '@/router/routes'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { ChevronRight, Plus, ArrowRight, ChevronLeft } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import { Switch } from '@/components/ui/switch'
  import { Card, CardContent } from '@/components/ui/card'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import MachineLearning from '@/components/svg/Illustrations/MachineLearning.vue'
  import { PhoneForwardingRulesService } from '@/types/generated/services.gen'
  import type { PhoneForwardingRuleList, CustomerUser as User } from '@/types/generated/types.gen'
  import { PhonebotSelfServicePlanEnum, RoleEnum } from '@/types/generated/types.gen'
  import { useToast } from '@/components/ui/toast'
  import { usePhoneNumbers } from '@/composables/usePhoneNumbers'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { useUserStore } from '@/shared/stores/userStore'

  const { t } = useTranslation({
    en: {
      phoneCallRules: 'Phone Call Rules',
      phoneForwardingRules: 'Phone Forwarding Rules',
      aiPhoneAnswering: 'AI Phone Answering',
      newRule: 'New Rule',
      active: 'Active',
      inactive: 'Inactive',
      allFeminine: 'All',
      allMasculine: 'All',
      rulesBasedOnPhoneNumbers: 'Phone numbers',
      rulesBasedOnEntityType: 'Entity types',
      rulesBasedOnTextContext: 'Text context',
      ruleTypePhoneNumbers: 'phone number based',
      ruleTypeEntityTypes: 'entity type based',
      ruleTypeTextContext: 'text context based',
      youHaveNoPhoneCallRules: 'You have no phone call rules',
      setTheFirstRule: 'Set the first rule',
      ruleTitle: 'Rule Title',
      whenCallComesFrom: 'When a call comes from one of these numbers:',
      itWillBeForwardedTo: 'It will be forwarded to:',
      messageBeforeForwarding: 'Message Before Forwarding',
      noForwardingMessage: 'No forwarding message, please set a message',
      edit: 'Edit',
      delete: 'Delete',
      loadingRules: 'Loading rules...',
      loadingRulesHint: 'Please wait while we fetch your phone forwarding rules',
      errorLoadingRules: 'Error loading rules',
      errorUpdatingRule: 'Error updating rule',
      errorDeletingRule: 'Error deleting rule',
      errorCreatingRule: 'Error creating rule',
      ruleUpdated: 'Rule updated successfully',
      ruleDeleted: 'Rule deleted successfully',
      untitledRule: 'Untitled Rule',
      noRulesMessage: 'You have no {type} rules',
      setFirstRuleMessage: 'Set the first rule',
      whenCallerIdentifiesAs: 'When the caller identifies as:',
      whenConditionIsMet: 'When the condition is met:',
      appliedToPhoneNumbers: 'Applied to phone numbers',
      showMore: 'Show more',
      showLess: 'Show less',
      judge: 'Judge',
      court: 'Court',
      witness: 'Witness',
      lawyer: 'Lawyer',
      prosecutor: 'Prosecutor',
      foreignersAuthority: 'Foreigners Authority',
      expert: 'Expert',
      search: 'Search',
      of: 'of',
      previous: 'Previous',
      next: 'Next',
      results: 'results',
      sortBy: 'Sort by',
      newest: 'Newest first',
      oldest: 'Oldest first',
      titleAsc: 'Title A-Z',
      titleDesc: 'Title Z-A',
      onlyAdminsCanCreate: 'Only administrators can create new rules',
      onlyAdminsCanModify: 'Only administrators can modify rules',
      trialInfoBox:
        'During your free trial you can fully use call forwarding rules. After your trial ends, you will need to upgrade to the “Professional” plan to continue editing these rules.'
    },
    de: {
      phoneCallRules: 'Anrufweiterleitungsregeln',
      phoneForwardingRules: 'Anrufweiterleitungsregeln',
      aiPhoneAnswering: 'KI-Anrufbeantworter',
      newRule: 'Neue Regel',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      allFeminine: 'Alle',
      allMasculine: 'Alle',
      rulesBasedOnPhoneNumbers: 'Telefonnummern',
      rulesBasedOnEntityType: 'Entitätstypen',
      rulesBasedOnTextContext: 'Textkontext',
      ruleTypePhoneNumbers: 'Telefonnummern',
      ruleTypeEntityTypes: 'Entitätstypen',
      ruleTypeTextContext: 'Textkontext',
      youHaveNoPhoneCallRules: 'Sie haben keine Anrufweiterleitungsregeln',
      setTheFirstRule: 'Erste Regel erstellen',
      ruleTitle: 'Regel-Titel',
      whenCallComesFrom: 'Wenn ein Anruf von einer dieser Nummern kommt:',
      itWillBeForwardedTo: 'Wird weitergeleitet an:',
      messageBeforeForwarding: 'Nachricht vor der Weiterleitung',
      noForwardingMessage: 'Keine Nachricht vor der Weiterleitung, bitte setzen Sie eine Nachricht',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      loadingRules: 'Regeln werden geladen...',
      loadingRulesHint: 'Bitte warten Sie, während wir Ihre Anrufweiterleitungsregeln abrufen',
      errorLoadingRules: 'Fehler beim Laden der Regeln',
      errorUpdatingRule: 'Fehler beim Aktualisieren der Regel',
      errorDeletingRule: 'Fehler beim Löschen der Regel',
      errorCreatingRule: 'Fehler beim Erstellen der Regel',
      ruleUpdated: 'Regel erfolgreich aktualisiert',
      ruleDeleted: 'Regel erfolgreich gelöscht',
      untitledRule: 'Unbenannte Regel',
      noRulesMessage: 'Sie haben keine {type} Regeln',
      setFirstRuleMessage: 'Erste Regel erstellen',
      whenCallerIdentifiesAs: 'Wenn sich der Anrufer identifiziert als:',
      whenConditionIsMet: 'Wenn die Bedingung erfüllt ist:',
      appliedToPhoneNumbers: 'Angewendet auf die Telefonnummern',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
      judge: 'Richter',
      court: 'Gericht',
      witness: 'Zeuge',
      lawyer: 'Anwalt',
      prosecutor: 'Staatsanwalt',
      foreignersAuthority: 'Ausländerbehörde',
      expert: 'Gutachter',
      search: 'Suche nach',
      of: 'von',
      previous: 'Zurück',
      next: 'Weiter',
      results: 'Einträge',
      sortBy: 'Sortieren nach',
      newest: 'Neueste zuerst',
      oldest: 'Älteste zuerst',
      titleAsc: 'Titel A-Z',
      titleDesc: 'Titel Z-A',
      onlyAdminsCanCreate: 'Nur Administratoren können neue Regeln anlegen',
      onlyAdminsCanModify: 'Nur Administratoren können Regeln ändern',
      trialInfoBox:
        'Während Ihrer kostenlosen Testphase können Sie die Anrufweiterleitungsregeln ohne Einschränkung nutzen. Nach Ablauf der Testphase benötigen Sie das „Professional“-Paket, um die Weiterleitungsregeln weiterhin bearbeiten zu können.'
    },
    fr: {
      phoneCallRules: "Règles d'appels",
      phoneForwardingRules: "Règles de transfert d'appels",
      aiPhoneAnswering: 'Réponse téléphonique par IA',
      newRule: 'Nouvelle règle',
      active: 'Actif',
      inactive: 'Inactif',
      allFeminine: 'Toutes',
      allMasculine: 'Tous',
      rulesBasedOnPhoneNumbers: 'Numéros de téléphone',
      rulesBasedOnEntityType: "Types d'entités",
      rulesBasedOnTextContext: 'Contexte textuel',
      ruleTypePhoneNumbers: 'basées sur les numéros de téléphone',
      ruleTypeEntityTypes: "basées sur le type d'entité",
      ruleTypeTextContext: 'basées sur le contexte textuel',
      youHaveNoPhoneCallRules: "Vous n'avez encore aucune règle d'appel",
      setTheFirstRule: 'Définir la première règle',
      ruleTitle: 'Titre de la règle',
      whenCallComesFrom: "Lorsqu'un appel provient de l'un de ces numéros :",
      itWillBeForwardedTo: "L'appel sera transféré vers :",
      messageBeforeForwarding: 'Message avant le transfert',
      noForwardingMessage: 'Aucun message de transfert défini. Veuillez en ajouter un.',
      edit: 'Modifier',
      delete: 'Supprimer',
      loadingRules: 'Chargement des règles...',
      loadingRulesHint: "Chargement des règles de transfert d'appels…",
      errorLoadingRules: 'Erreur lors du chargement des règles',
      errorUpdatingRule: 'Erreur lors de la mise à jour de la règle',
      errorDeletingRule: 'Erreur lors de la suppression de la règle',
      errorCreatingRule: 'Erreur lors de la création de la règle',
      ruleUpdated: 'La règle a été mise à jour avec succès',
      ruleDeleted: 'La règle a été supprimée avec succès',
      untitledRule: 'Règle sans titre',
      noRulesMessage: "Vous n'avez aucune règle {type}",
      setFirstRuleMessage: 'Définir la première règle',
      whenCallerIdentifiesAs: "Lorsque l'appelant est reconnu comme :",
      whenConditionIsMet: 'Lorsque la condition est remplie :',
      appliedToPhoneNumbers: 'Appliquée aux numéros de téléphone',
      showMore: 'Afficher plus',
      showLess: 'Afficher moins',
      judge: 'Juge',
      court: 'Tribunal',
      witness: 'Témoin',
      lawyer: 'Avocat',
      prosecutor: 'Procureur',
      foreignersAuthority: 'Autorité des étrangers',
      expert: 'Expert',
      search: 'Rechercher',
      of: 'sur',
      previous: 'Précédent',
      next: 'Suivant',
      results: 'résultats',
      sortBy: 'Trier par',
      newest: 'Plus récent en premier',
      oldest: 'Plus ancien en premier',
      titleAsc: 'Titre A-Z',
      titleDesc: 'Titre Z-A',
      onlyAdminsCanCreate: 'Seuls les administrateurs peuvent créer de nouvelles règles',
      onlyAdminsCanModify: 'Seuls les administrateurs peuvent modifier les règles',
      trialInfoBox:
        "Pendant votre période d'essai gratuite, vous pouvez utiliser pleinement les règles de transfert d'appel. Après la fin de votre période d'essai, vous devrez passer au plan \"Professionnel\" pour continuer à modifier ces règles."
    }
  })

  const router = useRouter()
  const { toast } = useToast()
  const { fetchUser } = useUserStore()
  const user = computed<User | undefined>(() => useUserStore().user as User | undefined)
  const isAdmin = computed(() => user.value?.role === RoleEnum.ADMIN)

  const rules = ref<PhoneForwardingRuleList[]>([])
  const loading = ref(true)
  const selectedFilter = ref('all')
  const expandedPhoneNumbers = ref<Set<string>>(new Set())

  // Pagination and search state
  const searchTerm = ref('')
  const currentPage = ref(1)
  const inputPage = ref('1')
  const limit = 10
  const totalCount = ref(0)
  const selectedOrdering = ref('-created_at')
  let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null

  // Sorting options
  const orderingOptions = computed(() => [
    { title: t('newest'), value: '-created_at' },
    { title: t('oldest'), value: 'created_at' },
    { title: t('titleAsc'), value: 'title' },
    { title: t('titleDesc'), value: '-title' }
  ])

  const { loadPhoneNumbers, getPhoneNumberDisplay } = usePhoneNumbers()

  const hasRules = computed(() => rules.value.length > 0)

  const filteredRules = computed(() => rules.value)

  const totalPages = computed(() => {
    return Math.ceil(totalCount.value / limit)
  })

  const hasNext = computed(() => currentPage.value < totalPages.value)
  const hasPrevious = computed(() => currentPage.value > 1)

  const ruleType = computed(() => {
    switch (selectedFilter.value) {
      case 'phonenumber':
        return t('ruleTypePhoneNumbers')
      case 'entity':
        return t('ruleTypeEntityTypes')
      case 'text':
        return t('ruleTypeTextContext')
      default:
        return ''
    }
  })

  const noRulesMessage = computed(() => {
    return t('noRulesMessage', { type: ruleType.value })
  })

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    searchTerm.value = value

    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
    }

    searchDebounceTimeout = setTimeout(async () => {
      currentPage.value = 1
      inputPage.value = '1'
      await loadRules()
    }, 300)
  }

  const loadRules = async () => {
    loading.value = true
    try {
      const offset = (currentPage.value - 1) * limit
      const params: any = {
        limit,
        offset,
        ordering: selectedOrdering.value
      }

      if (searchTerm.value) {
        params.search = searchTerm.value
      }

      if (selectedFilter.value !== 'all') {
        params.ruleType = selectedFilter.value
      }

      const response = await PhoneForwardingRulesService.phoneForwardingRulesList(params)
      rules.value = response.results as PhoneForwardingRuleList[]
      totalCount.value = response.count
    } catch (error) {
      console.error('Error loading rules:', error)
      toast({
        title: t('errorLoadingRules'),
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  }

  const createNewRule = () => {
    const query = selectedFilter.value !== 'all' ? { 'rule-type': selectedFilter.value } : {}
    router.push({
      path: ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULE_EDIT.replace(
        ':uuid',
        'new'
      ),
      query
    })
  }

  const editRule = (uuid: string) => {
    router.push(
      ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULE_EDIT.replace(':uuid', uuid)
    )
  }

  const deleteRule = async (uuid: string) => {
    try {
      await PhoneForwardingRulesService.phoneForwardingRulesDestroy({ uuid })
      toast({
        title: t('ruleDeleted')
      })
      await loadRules()
    } catch (error) {
      console.error('Error deleting rule:', error)
      toast({
        title: t('errorDeletingRule'),
        variant: 'destructive'
      })
    }
  }

  const getConditionLabel = (ruleType: string) => {
    switch (ruleType) {
      case 'phonenumber':
        return t('whenCallComesFrom')
      case 'entity':
        return t('whenCallerIdentifiesAs')
      case 'text':
        return t('whenConditionIsMet')
      default:
        return t('whenCallerIdentifiesAs')
    }
  }

  const getEntityTitle = (entityValue: string) => {
    const entityMap: Record<string, string> = {
      judge: t('judge'),
      court: t('court'),
      witness: t('witness'),
      lawyer: t('lawyer'),
      prosecutor: t('prosecutor'),
      foreigners_authority: t('foreignersAuthority'),
      expert: t('expert')
    }
    return entityMap[entityValue.toLowerCase()] || entityValue
  }

  const toggleRuleStatus = async (uuid: string, active: boolean) => {
    try {
      await PhoneForwardingRulesService.phoneForwardingRulesPartialUpdate({
        uuid,
        requestBody: { active }
      })

      const ruleIndex = rules.value.findIndex((rule) => rule.uuid === uuid)
      if (ruleIndex !== -1) {
        rules.value[ruleIndex] = { ...rules.value[ruleIndex], active }
      }

      toast({
        title: t('ruleUpdated')
      })
    } catch (error) {
      console.error('Error updating rule:', error)
      toast({
        title: t('errorUpdatingRule'),
        variant: 'destructive'
      })
    }
  }

  const togglePhoneNumbers = (uuid: string) => {
    const next = new Set(expandedPhoneNumbers.value)
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    expandedPhoneNumbers.value = next
  }

  const handlePageChange = async (page: number) => {
    currentPage.value = page
    inputPage.value = page.toString()
    await loadRules()
  }

  const goToPage = () => {
    const pageNumber = parseInt(inputPage.value)
    if (pageNumber >= 1 && pageNumber <= totalPages.value) {
      handlePageChange(pageNumber)
    } else {
      inputPage.value = currentPage.value.toString()
    }
  }

  const handleSortOptionSelect = (optionValue: string) => {
    selectedOrdering.value = optionValue
    loadRules()
  }

  // Trial info box logic (same conditions as in SettingsBase)
  const isPhoneLitePlan = computed(() => {
    return (
      user.value?.org.is_phonebot_self_service_customer &&
      user.value?.org.phonebot_self_service_plan === PhonebotSelfServicePlanEnum.PHONE_BEGINNER_PLAN
    )
  })

  const isInTrialPeriod = computed(() => {
    if (!user.value?.org.phonebot_trial_ends_at) return false

    const trialDateStr = user.value.org.phonebot_trial_ends_at as unknown as string
    let trialEndsAt: Date

    if (trialDateStr.includes('.')) {
      const [day, month, year] = trialDateStr.split('.')
      trialEndsAt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    } else {
      trialEndsAt = new Date(trialDateStr)
    }

    return trialEndsAt > new Date()
  })

  const showTrialInfoBox = computed(() => {
    return isPhoneLitePlan.value && isInTrialPeriod.value
  })

  // Watch for filter changes
  watch(selectedFilter, async () => {
    currentPage.value = 1
    inputPage.value = '1'
    await loadRules()
  })

  onMounted(async () => {
    const fetching: Promise<unknown>[] = []

    if (!user.value) {
      fetching.push(fetchUser())
    }

    fetching.push(loadRules())
    fetching.push(loadPhoneNumbers())

    await Promise.all(fetching)
  })
</script>

<template>
  <TooltipProvider>
    <SettingsPage>
      <template #header>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <nav class="flex items-center gap-2 text-sm text-gray-500">
              <div>{{ t('aiPhoneAnswering') }}</div>
              <ChevronRight class="size-3" />
              <span class="font-medium text-gray-900">{{ t('phoneCallRules') }}</span>
            </nav>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <Card
          v-if="showTrialInfoBox"
          class="border-blue-200 bg-blue-50"
        >
          <CardContent class="py-3">
            <p class="text-sm text-blue-900">
              {{ t('trialInfoBox') }}
            </p>
          </CardContent>
        </Card>

        <!-- Search Input and Sort -->
        <div class="flex w-full flex-col gap-y-2 md:flex-row md:items-center md:gap-x-3">
          <div class="relative w-full md:min-w-[200px] md:max-w-md">
            <div
              class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
            >
              <svg
                class="size-4 flex-shrink-0 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                ></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
            </div>
            <input
              type="search"
              :placeholder="t('search')"
              class="block w-full rounded-lg border border-gray-200 bg-gray-50 pe-4 ps-11 text-sm text-gray-800
                focus:border-purple-500 focus:ring-purple-500 dark:border-neutral-700 dark:bg-neutral-800
                dark:text-neutral-400 dark:placeholder:text-neutral-500 dark:focus:border-purple-500"
              :value="searchTerm"
              data-cy="PhoneForwardingRuleSettingsSearchInput"
              @input="handleSearch"
            />
          </div>

          <!-- Sort Dropdown -->
          <SelectField
            v-model="selectedOrdering"
            name="sort-order"
            class="min-w-32"
            :options="orderingOptions"
            data-cy="PhoneForwardingRuleSettingsSortField"
            @update:model-value="handleSortOptionSelect"
          />
        </div>

        <!-- Filter Tabs -->
        <div class="flex items-center justify-between">
          <div class="flex border-b border-gray-200">
            <button
              :class="[
                'border-b-2 px-6 py-2 text-sm font-medium transition-colors',
                selectedFilter === 'all'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
              data-cy="PhoneForwardingRuleSettingsAllTab"
              @click="selectedFilter = 'all'"
            >
              {{ t('allFeminine') }}
            </button>
            <button
              :class="[
                'border-b-2 px-6 py-2 text-sm font-medium transition-colors',
                selectedFilter === 'phonenumber'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
              data-cy="PhoneForwardingRuleSettingsPhoneNumberTab"
              @click="selectedFilter = 'phonenumber'"
            >
              {{ t('rulesBasedOnPhoneNumbers') }}
            </button>
            <button
              :class="[
                'border-b-2 px-6 py-2 text-sm font-medium transition-colors',
                selectedFilter === 'entity'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
              data-cy="PhoneForwardingRuleSettingsEntityTab"
              @click="selectedFilter = 'entity'"
            >
              {{ t('rulesBasedOnEntityType') }}
            </button>
            <button
              :class="[
                'border-b-2 px-6 py-2 text-sm font-medium transition-colors',
                selectedFilter === 'text'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
              data-cy="PhoneForwardingRuleSettingsTextTab"
              @click="selectedFilter = 'text'"
            >
              {{ t('rulesBasedOnTextContext') }}
            </button>
          </div>
          <Tooltip v-if="hasRules">
            <TooltipTrigger as-child>
              <span class="inline-flex">
                <Button
                  variant="secondary"
                  :disabled="!isAdmin"
                  data-cy="PhoneForwardingRuleSettingsNewRuleButton"
                  @click="createNewRule"
                >
                  <Plus class="mr-2 size-4" />
                  {{ t('newRule') }}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent
              v-if="!isAdmin"
              variant="secondary"
            >
              <p>{{ t('onlyAdminsCanCreate') }}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <!-- Rules Content -->
        <div class="mt-4">
          <div
            v-if="loading"
            class="flex flex-col items-center justify-center gap-6 rounded-xl border border-gray-200 bg-white p-16
              shadow-sm"
          >
            <div class="flex flex-col items-center text-center">
              <div
                class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100"
              >
                <div
                  class="h-8 w-8 !animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
                ></div>
              </div>
              <h3 class="mb-2 text-lg font-semibold text-gray-900">{{ t('loadingRules') }}</h3>
              <p class="text-sm text-gray-500">{{ t('loadingRulesHint') }}</p>
            </div>
          </div>
          <div
            v-else-if="!hasRules"
            class="flex flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-16
              shadow-sm"
          >
            <div class="flex flex-col items-center text-center">
              <h3 class="mb-2 text-lg font-semibold text-gray-900">
                {{ noRulesMessage }}
              </h3>
              <div class="mb-4 flex h-64 w-64 items-center justify-center rounded-lg">
                <MachineLearning />
              </div>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex">
                    <Button
                      data-cy="PhoneForwardingRuleSettingsSetFirstRuleButton"
                      variant="secondary"
                      :disabled="!isAdmin"
                      @click="createNewRule"
                    >
                      <Plus class="mr-2 size-4" />
                      {{ t('setTheFirstRule') }}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  v-if="!isAdmin"
                  variant="secondary"
                >
                  <p>{{ t('onlyAdminsCanCreate') }}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div
            v-else
            class="space-y-4"
          >
            <!-- Rule cards -->
            <div class="space-y-4">
              <Card
                v-for="rule in filteredRules"
                :key="rule.uuid"
                class="border border-gray-200 shadow-sm"
              >
                <CardContent class="p-3">
                  <div class="mb-3 flex items-center justify-between">
                    <h3
                      v-if="rule.title"
                      class="text-base font-medium text-gray-900"
                    >
                      {{ rule.title }}
                    </h3>
                    <h3
                      v-else
                      class="text-base font-medium text-gray-500"
                    >
                      {{ t('untitledRule') }}
                    </h3>
                    <div class="flex items-center gap-3">
                      <!-- Active/Inactive toggle -->
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <div class="inline-flex">
                            <div
                              class="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-2 py-1"
                            >
                              <Switch
                                :checked="rule.active"
                                :disabled="!isAdmin"
                                data-cy="PhoneForwardingRuleSettingsRuleToggle"
                                @update:checked="(checked) => toggleRuleStatus(rule.uuid, checked)"
                              />
                              <span class="text-xs font-medium text-purple-800">
                                {{ t('active') }}
                              </span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          v-if="!isAdmin"
                          variant="secondary"
                        >
                          <p>{{ t('onlyAdminsCanModify') }}</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <span class="inline-flex">
                            <Button
                              variant="secondary"
                              size="sm"
                              :disabled="!isAdmin"
                              data-cy="PhoneForwardingRuleSettingsEditButton"
                              @click="editRule(rule.uuid)"
                            >
                              <svg
                                class="size-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          v-if="!isAdmin"
                          variant="secondary"
                        >
                          <p>{{ t('onlyAdminsCanModify') }}</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <span class="inline-flex">
                            <Button
                              variant="secondary"
                              size="sm"
                              :disabled="!isAdmin"
                              data-cy="PhoneForwardingRuleSettingsDeleteButton"
                              @click="deleteRule(rule.uuid)"
                            >
                              <svg
                                class="size-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          v-if="!isAdmin"
                          variant="secondary"
                        >
                          <p>{{ t('onlyAdminsCanModify') }}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  <!-- Rule Content -->
                  <div class="mb-3 rounded-lg bg-gray-50 p-3">
                    <div class="mb-3 flex items-start gap-2">
                      <span class="text-sm font-medium text-gray-700">
                        {{ getConditionLabel(rule.rule_type) }}
                      </span>
                      <div class="flex flex-wrap gap-1">
                        <template v-if="rule.rule_type === 'phonenumber' && rule.from_numbers">
                          <Badge
                            v-for="phoneNumber in rule.from_numbers"
                            :key="phoneNumber"
                            variant="secondary"
                            class="text-xs"
                          >
                            {{ phoneNumber }}
                          </Badge>
                        </template>
                        <template v-if="rule.rule_type === 'entity' && rule.from_entity_types">
                          <Badge
                            v-for="entityType in rule.from_entity_types"
                            :key="(entityType as any).title"
                            variant="secondary"
                            class="text-xs"
                          >
                            {{ getEntityTitle((entityType as any).title) }}
                          </Badge>
                        </template>
                        <Badge
                          v-if="rule.rule_type === 'text' && rule.from_text_context"
                          variant="secondary"
                          class="text-xs"
                        >
                          {{ rule.from_text_context }}
                        </Badge>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <ArrowRight class="h-4 w-4 text-gray-400" />
                      <span class="text-sm font-medium text-gray-700">
                        {{ t('itWillBeForwardedTo') }} {{ rule.target_phone_number }}
                      </span>
                    </div>
                  </div>

                  <!-- Forwarding Message -->
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-900">
                      {{ t('messageBeforeForwarding') }}
                    </label>
                    <div class="rounded-lg bg-gray-50 p-3">
                      <p
                        v-if="rule.forwarding_message"
                        class="text-sm font-medium text-gray-700"
                      >
                        {{ rule.forwarding_message }}
                      </p>
                      <p
                        v-else
                        class="text-sm text-gray-500"
                      >
                        {{ t('noForwardingMessage') }}
                      </p>
                    </div>
                  </div>

                  <!-- Applied Phone Numbers -->
                  <div v-if="rule.phone_number_uuids && rule.phone_number_uuids.length > 0">
                    <label class="mb-2 block text-sm font-medium text-gray-900">
                      {{ t('appliedToPhoneNumbers') }}
                    </label>
                    <div class="rounded-lg bg-gray-50 p-3">
                      <div class="flex flex-wrap gap-1">
                        <template
                          v-if="
                            rule.phone_number_uuids.length <= 3 ||
                            expandedPhoneNumbers.has(rule.uuid)
                          "
                        >
                          <Badge
                            v-for="phoneNumberUuid in rule.phone_number_uuids"
                            :key="phoneNumberUuid"
                            variant="secondary"
                            class="text-xs"
                          >
                            {{ getPhoneNumberDisplay(phoneNumberUuid) }}
                          </Badge>
                        </template>
                        <template v-else>
                          <Badge
                            v-for="phoneNumberUuid in rule.phone_number_uuids.slice(0, 2)"
                            :key="phoneNumberUuid"
                            variant="secondary"
                            class="text-xs"
                          >
                            {{ getPhoneNumberDisplay(phoneNumberUuid) }}
                          </Badge>
                          <button
                            class="text-xs text-purple-600 hover:text-purple-800"
                            @click="togglePhoneNumbers(rule.uuid)"
                          >
                            +{{ rule.phone_number_uuids.length - 2 }} {{ t('showMore') }}
                          </button>
                        </template>
                      </div>
                      <button
                        v-if="
                          rule.phone_number_uuids.length > 3 && expandedPhoneNumbers.has(rule.uuid)
                        "
                        class="mt-2 text-xs text-purple-600 hover:text-purple-800"
                        @click="togglePhoneNumbers(rule.uuid)"
                      >
                        {{ t('showLess') }}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <!-- Pagination Controls -->
            <div
              v-if="hasRules"
              class="mt-4 flex flex-col-reverse gap-y-3 sm:grid sm:grid-cols-2 sm:items-center sm:gap-x-5"
            >
              <!-- Results Count -->
              <p class="text-sm text-gray-800">
                <span class="font-medium">{{ totalCount }}</span>
                <span class="ps-2 text-gray-500">{{ t('results') }}</span>
              </p>

              <!-- Navigation Controls -->
              <nav class="flex items-center justify-end gap-x-1">
                <button
                  class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
                    text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
                    disabled:pointer-events-none disabled:opacity-50"
                  :disabled="!hasPrevious"
                  type="button"
                  data-cy="PhoneForwardingRuleSettingsPreviousButton"
                  @click="handlePageChange(currentPage - 1)"
                >
                  <ChevronLeft class="mt-0.5 size-3.5 flex-shrink-0" />
                  <span
                    class="sr-only"
                    aria-hidden="true"
                  >
                    {{ t('previous') }}
                  </span>
                </button>
                <div class="flex items-center gap-x-1">
                  <input
                    v-model="inputPage"
                    class="flex h-8 w-12 items-center justify-center rounded-lg border-gray-200 bg-gray-50 px-3 py-2 text-sm
                      text-gray-800 ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                    type="text"
                    data-cy="PhoneForwardingRuleSettingsPageInput"
                    @keyup.enter="goToPage"
                  />
                  <span
                    class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
                  >
                    {{ t('of') }}
                  </span>
                  <span
                    class="flex min-h-[38px] items-center justify-center px-1.5 py-2 text-sm text-gray-500"
                  >
                    {{ totalPages }}
                  </span>
                </div>
                <button
                  class="inline-flex min-h-[38px] min-w-[38px] items-center justify-center gap-x-2 rounded-lg px-2.5 py-2
                    text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
                    disabled:pointer-events-none disabled:opacity-50"
                  :disabled="!hasNext"
                  type="button"
                  data-cy="PhoneForwardingRuleSettingsNextButton"
                  @click="handlePageChange(currentPage + 1)"
                >
                  <span
                    class="sr-only"
                    aria-hidden="true"
                  >
                    {{ t('next') }}
                  </span>
                  <ChevronRight class="mt-0.5 size-3.5 flex-shrink-0" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </SettingsPage>
  </TooltipProvider>
</template>
